import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import { Button, Link, Modal, Timeout, Typography } from 'components';
import ConnectionFailedIcon from 'assets/icons/connection-failed.svg?react';
import PhoneIcon from 'assets/icons/phone.svg?react';
import PhoneWarningIcon from 'assets/icons/phone-warning.svg?react';
import { useDocumentUpload } from 'hooks/webapi';
import ConnectionLottie from 'assets/lottie/connection.json';
import styles from './MagicLinkModal.module.scss';

const connectionLottieOptions = {
  loop: true,
  autoplay: true,
  animationData: ConnectionLottie,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

type Props = {
  onSuccess: () => void;
  onCancel: () => void;
  dataTest?: string;
};

type Status = 'CONNECTING' | 'EXPIRED' | 'PROCESSING' | 'FAILED';

const COUNTDOWN = 60 * 2; // s
const POLLING_INTERVAL = 1000 * 2; // ms

const MagicLinkModalMemo = memo(function MagicLinkModal({
  onSuccess,
  onCancel,
  dataTest,
}: Props) {
  const { t } = useTranslation([
    'identification_conecting',
    'identification_processing',
    'identification_failed',
    'identification_expired',
  ]);
  const { sendSms, getUploadStatus } = useDocumentUpload();
  const [status, setStatus] = useState<Status>('CONNECTING');

  const asyncSendSms = useCallback(async () => {
    try {
      await sendSms();
    } catch {
      setStatus('FAILED');
    }
  }, [sendSms]);

  useEffect(() => {
    asyncSendSms();
  }, [asyncSendSms]);

  const asyncUploadStatus = useCallback(async () => {
    try {
      const uploadStatus = await getUploadStatus();

      if (!uploadStatus) {
        return;
      }

      if (uploadStatus.status === 'USER_STARTED') {
        setStatus('PROCESSING');
      } else if (uploadStatus.status === 'DONE') {
        if (onSuccess) {
          onSuccess();
        }
      } else if (uploadStatus.status === 'EXPIRED') {
        setStatus('EXPIRED');
      } else if (uploadStatus?.status === 'ERROR') {
        setStatus('FAILED');
      }
    } catch {
      onCancel();
    }
  }, [getUploadStatus, onSuccess, onCancel]);

  useEffect(() => {
    let polling: NodeJS.Timeout | undefined;
    (() => {
      if (status !== 'CONNECTING' && status !== 'PROCESSING') return;
      polling = setInterval(async () => {
        await asyncUploadStatus();
      }, POLLING_INTERVAL);
    })();

    return () => {
      clearInterval(polling);
    };
  }, [status, asyncUploadStatus]);

  const handleRetry = useCallback(() => {
    setStatus('CONNECTING');
    asyncSendSms();
  }, [asyncSendSms]);

  const handleTimeout = useCallback(() => {
    setStatus('EXPIRED');
  }, []);

  return (
    <Modal
      dataTest={dataTest}
      className={styles.card}
      contentClassName={styles.cardContent}
    >
      {status === 'CONNECTING' && (
        <>
          <PhoneIcon className={styles.icon} />
          <Typography className={styles.title} size="l" kind="h3">
            {t('title')}
          </Typography>
          <Typography className={styles.hint}>{t('hint')}</Typography>
          <Typography className={styles['time-info']}>{t('time')}</Typography>
          <Timeout seconds={COUNTDOWN} onTimeout={handleTimeout} />
        </>
      )}

      {status === 'EXPIRED' && (
        <>
          <PhoneWarningIcon className={styles.icon} />
          <Typography className={styles.title} size="l" kind="h3">
            {t('identification_expired:title')}
          </Typography>
          <Button className={styles.button} onClick={handleRetry}>
            {t('identification_expired:confirm')}
          </Button>
          <Link onClick={onCancel}>{t('identification_expired:close')}</Link>
        </>
      )}

      {status === 'PROCESSING' && (
        <>
          <div className={styles.connecting}>
            <Lottie options={connectionLottieOptions} />
          </div>
          <Typography className={styles.title} size="l" kind="h3">
            {t('identification_processing:title')}
          </Typography>
          <Typography className={styles.hint}>
            {t('identification_processing:hint')}
          </Typography>
        </>
      )}

      {status === 'FAILED' && (
        <>
          <ConnectionFailedIcon className={styles.icon} />
          <Typography className={styles.title} size="l" kind="h3">
            {t('identification_failed:title')}
          </Typography>
          <Button onClick={handleRetry}>
            {t('identification_failed:retry')}
          </Button>
        </>
      )}
    </Modal>
  );
});

export default MagicLinkModalMemo;
